import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { PopoverModule } from 'primeng/popover';
import { SelectModule } from 'primeng/select';
import { SliderModule } from 'primeng/slider';
import { SplitButtonModule } from 'primeng/splitbutton';
import { TableModule } from 'primeng/table';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { TooltipModule } from 'primeng/tooltip';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { AlertComponent } from '../alert/alert.component';
import { NoteEditBase } from './note-edit-base';
import { FileUploadComponent } from '../file-upload/file-upload.component';
import { RichEditorComponent } from '../rich-editor/rich-editor.component';
import { AppConfig } from '../user-settings/app.config';
import { NoteHeaderComponent } from './note-header.component';
import { NoteShellComponent } from './note-shell.component';
import { NoteEmptyStateComponent } from './note-empty-state.component';
import { NotePanelDirective } from './note-panel.directive';
import { NOTE_TAB_STATE_KEY, NoteTab, initialNoteTab } from './note.types';
import { SelectButtonModule } from 'primeng/selectbutton';
import { EDITOR_SURFACE_CLASSES, saveStatusTextClass } from './note.styles';
import { NOTE_SCROLL_STATE_KEY, captureNoteScrollAnchor, readNoteScrollAnchor, restoreNoteScrollAnchor } from './note-scroll-anchor';
import { focusTaskWhenReady, readFocusTaskId } from './note-task-focus';

/**
 * The Edit Note page (`/edit/:_id`).
 *
 * It extends `NoteEditBase`, so every piece of edit-mode behaviour is inherited rather than copied:
 * loading and form setup, autosave with its save/sync status lifecycle, Ctrl+S, the unsaved-changes and
 * discard flows, edit-session soft locking, attachments, tag and related-note management, AI helpers,
 * save-as-template and the page menu. This class supplies only the template, built on the shared
 * note shell (the same header, tabs and layout as the View page).
 */
@Component({
  selector: 'app-note-edit',
  templateUrl: './note-edit.component.html',
  host: { class: 'block h-dvh' },
  // The parent's providers are not inherited by subclasses, and its constructor needs them.
  providers: [ConfirmationService],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    ButtonModule,
    ConfirmDialogModule,
    DatePickerModule,
    ToggleSwitchModule,
    DialogModule,
    InputTextModule,
    PopoverModule,
    SelectModule,
    SelectButtonModule,
    SliderModule,
    SplitButtonModule,
    TableModule,
    TieredMenuModule,
    TooltipModule,
    AutoCompleteModule,
    AlertComponent,
    AppConfig,
    FileUploadComponent,
    RichEditorComponent,
    NoteShellComponent,
    NoteHeaderComponent,
    NotePanelDirective,
    NoteEmptyStateComponent,
  ],
})
export class NoteEditComponent extends NoteEditBase implements OnInit, OnDestroy {
  @ViewChild(NoteShellComponent) private noteShell?: NoteShellComponent;
  private stopScrollRestore: (() => void) | null = null;
  private stopTaskFocus: (() => void) | null = null;

  private readonly activatedRoute = inject(ActivatedRoute);

  protected readonly editorSurfaceClasses = EDITOR_SURFACE_CLASSES;
  /** The attachment uploader must exist even if its tab is never opened: save/flush logic queries it. */
  protected readonly eagerTabs = ['note', 'attachments'] as const;

  /** Currently selected tab. The Note tab (writing) is the default. */
  activeNoteTab = 'note';

  private tabsCacheKey = '';
  private tabsCache: NoteTab[] = [];

  override ngOnInit(): void {
    super.ngOnInit();
    // The tab carried over from View (same note), else a ?tab= deep link, else Note.
    this.activeNoteTab = initialNoteTab(history.state, this.activatedRoute.snapshot.queryParamMap.get('tab'));
    // Coming from View of this note: open the editor at the same reading position.
    const anchor = readNoteScrollAnchor(history.state);
    // Opened by clicking a task in the Home Tasks view: the cursor goes to the end of that task.
    const focusTaskId = readFocusTaskId(history.state);
    if (focusTaskId) {
      this.activeNoteTab = 'note';
      this.stopTaskFocus = focusTaskWhenReady(() => this.descriptionEditor?.focusInlineTask(focusTaskId) ?? false);
    } else if (anchor && this.activeNoteTab === 'note') {
      this.stopScrollRestore = restoreNoteScrollAnchor(() => this.noteShell?.scrollElement ?? null, anchor);
    }
  }

  override ngOnDestroy(): void {
    this.stopScrollRestore?.();
    this.stopTaskFocus?.();
    super.ngOnDestroy();
  }

  /** Switching to View keeps the open tab (see initialNoteTab) and the reading position (see note-scroll-anchor.ts). */
  protected override modeSwitchState(): Record<string, unknown> {
    return {
      [NOTE_TAB_STATE_KEY]: this.activeNoteTab,
      [NOTE_SCROLL_STATE_KEY]: this.activeNoteTab === 'note' ? captureNoteScrollAnchor(this.noteShell?.scrollElement) : null,
    };
  }

  /** True once the note and its form exist; panels show a skeleton until then. */
  get isReady(): boolean {
    return !!this.note && !!this.editNoteForm && !this.isLoading();
  }

  /** Live title for the header (the form value leads the persisted note while typing). */
  get headerTitle(): string {
    const typed = this.editNoteForm?.get('Title')?.value;
    return (typeof typed === 'string' ? typed : this.note?.Title) ?? '';
  }

  /** Tab definitions with live counts, re-created only when a count changes (the shell is OnPush). */
  get noteTabs(): NoteTab[] {
    const key = `${this.attachmentCount}|${this.relatedNotes.length}`;
    if (key !== this.tabsCacheKey) {
      this.tabsCacheKey = key;
      this.tabsCache = [
        { id: 'note', label: 'Note' },
        { id: 'properties', label: 'Properties' },
        { id: 'attachments', label: 'Attachments', count: this.attachmentCount },
        { id: 'related', label: 'Related', count: this.relatedNotes.length },
        { id: 'comments', label: 'Comments' },
        { id: 'history', label: 'History' },
      ];
    }
    return this.tabsCache;
  }

  /** Tailwind text colour for the footer save/sync indicator. */
  get saveStatusTextClass(): string {
    return saveStatusTextClass(this.autosaveStatusView.colorClass);
  }

  /** Non-autosave mode keeps its own "Unsaved" hint; both modes share the footer. */
  get hasUnsavedEdits(): boolean {
    return this.isContentChanged;
  }
}
